/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo } from 'react'
import { Table, Pagination } from 'react-bootstrap'
import './LabSharedTable.css'

type Column = {
  key: string
  label: string
  formatter?: (value: any, row: any) => React.ReactNode
  style?: React.CSSProperties
  sortable?: boolean
  headerClassName?: string
}

type SortConfig = {
  key: string | null
  direction: 'asc' | 'desc'
}

type LabSharedTableProps = {
  columns: Column[]
  data: any[]
  additionalRows?: (row: any) => React.ReactNode
  className?: string
  rowClassName?: (row: any) => string
  onRowClick?: (row: any, event: React.MouseEvent<HTMLTableRowElement>) => void
  pageSize?: number
}

const LabSharedTable: React.FC<LabSharedTableProps> = ({
  columns,
  data,
  additionalRows,
  className,
  rowClassName,
  pageSize = 10,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: 'asc',
  })

  const handleSort = (key: string): void => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    )
  }

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return [...data]
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key!]
      const bVal = b[sortConfig.key!]
      if (aVal == null) return 1
      if (bVal == null) return -1
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
      }
      return sortConfig.direction === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal))
    })
  }, [data, sortConfig])

  const totalPages = pageSize > 0 ? Math.ceil(sortedData.length / pageSize) : 1

  const paginatedData = useMemo(() => {
    if (pageSize <= 0) return sortedData
    const start = (currentPage - 1) * pageSize
    return sortedData.slice(start, start + pageSize)
  }, [sortedData, currentPage, pageSize])

  const handlePageChange = (page: number): void => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  return (
    <>
      <div className="lab-table-wrapper">
        <Table hover responsive className={`lab-table ${className || ''}`}>
          <thead className="table-shared-light">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    ...col.style,
                    cursor: col.sortable ? 'pointer' : 'default',
                  }}
                  className={col.headerClassName || ''}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  {col.label}
                  {col.sortable && sortConfig.key === col.key && (
                    <span className="sort-icon">
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center text-muted">
                  No lab data found
                </td>
              </tr>
            ) : (
              paginatedData.map((row, rowIndex) => {
                const rowKey =
                  row.id || row.orderNo || `${rowIndex}-${JSON.stringify(row)}`
                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      className={rowClassName ? rowClassName(row) : ''}
                      style={{ cursor: 'default' }}
                    >
                      {columns.map((col) => {
                        const raw = row[col.key]
                        const content = col.formatter
                          ? col.formatter(raw, row)
                          : (raw ?? '—')
                        return (
                          <td key={col.key} style={col.style}>
                            {content}
                          </td>
                        )
                      })}
                    </tr>
                    {additionalRows && additionalRows(row)}
                  </React.Fragment>
                )
              })
            )}
          </tbody>
        </Table>
      </div>

      {pageSize > 0 && (
        <div className="lab-pagination d-flex justify-content-start">
          <Pagination className="mb-0">
            <Pagination.First
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            <Pagination.Item active>{currentPage}</Pagination.Item>
            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}
    </>
  )
}

export default React.memo(LabSharedTable)
